<template>
  <b-container fluid v-if='configurableMaterial'>
    <b-row>
      <b-col md="12">
        <b-card-group columns v-if="!configuration.Layout">
          <template v-for="value in configuration.Values">
            <configurable-material-value :value="value" :key="value.Characteristic" v-if="configuration.Invisible.indexOf(value.Characteristic) < 0 && !configuration.Characteristics[value.Characteristic].Hidden"></configurable-material-value>
          </template>
        </b-card-group>
        <template v-if="configuration.Layout">
          <template v-for="(tab, tabIndex) in getTabs()" >
            <template v-if="!tab.OnlyVisibleForCustomerAdmin || _isCustomerAdminSite()">
              <template v-for="(row, index) in getRows(tab.Title, tabIndex)">
                <div :key="tabIndex + 'row'+index" :class="{ 'row': true, 'custom-border-container': row.DisplayTitle }" v-if="row.Visible && checkVisibility(row)">
                  <span v-if="row.DisplayTitle" class="custom-border-title">{{ row.Title }}</span>
                  <div class="mt-3 col-12" v-if="row.Note" :key="'note' + index" style="white-space: pre-wrap;">{{ row.Note }}</div>
                  <template v-for="item in row.Items">
                    <template v-for="value in [getConfigurationValue(item, null)]">
                      <div :class="'mt-3 col' + calculateCol(row.ColumnCount)" v-if="value && configuration.Invisible.indexOf(value.Characteristic) < 0 && !configuration.Characteristics[value.Characteristic].Hidden" :key="item+value.Characteristic">
                        <configurable-material-value :value="value"></configurable-material-value>
                      </div>
                    </template>
                  </template>
                </div>
              </template>
          </template>
        </template>
        </template>
      </b-col>
    </b-row>
  </b-container>
</template>

<script lang="ts">
import MxSite from '@/mixins/site'
import LoadingText from '@/components/utility/loading-text.vue'
import Multiselect from 'vue-multiselect'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { namespace } from 'vuex-class'
import { Model } from '@/store/editor-model/types'
import { CartItem } from '@/store/cart/types'
import { ISapConfiguratorValue, ISapConfigureMaterialRequest } from '@/store/configurable-material/types'
import ConfigurableMaterialValue from '@/components/configurable-material/configurable-material-value.vue'
import { isCustomerAdminSite } from '@/helpers/sites.helper'

const nsCart = namespace('cart')
const nsMultiplier = namespace('multiplier')
const nsConfigurableMaterial = namespace('configurable-material')

@Component({
  components: {
    'loading-text': LoadingText,
    'configurable-material-value': ConfigurableMaterialValue,
    Multiselect,
  },
})
export default class extends MxSite {
  @Prop()
  private value: any

  @Prop()
  private customerNumber: any

  @Prop()
  private divisionCode: any

  @nsConfigurableMaterial.Action('getConfiguration')
  private getConfiguration: any

  @nsConfigurableMaterial.Action('resetRequest')
  private resetRequest: any

  @nsConfigurableMaterial.Getter('request')
  public request!: ISapConfigureMaterialRequest

  @nsConfigurableMaterial.Getter('configuration')
  public configuration: any

  @nsConfigurableMaterial.Getter('configurationLoaded')
  public configurationLoaded: any

  @nsConfigurableMaterial.Action('clearConfiguration')
  private clearConfiguration: any

  @nsCart.Action('saveCartItem')
  private saveCartItem: any

  @nsMultiplier.Getter('getMultiplierPrice')
  private getMultiplierPrice: any

  public configurableMaterial: any = null
  public userValues: any = []
  public currentVisibleCharacteristics: any = []

  public mounted() {
    this.configurableMaterial = {
      MaterialNumber: this.value.MaterialNumber,
      TotalPrice: 0,
      Inconsistent: 0,
      Incomplete: 0,
      OrderItems: [],
      SpecialPrices: this.value.SpecialPrices ?? [],
      UserValues: this.value.UserValues ?? [],
    }
    this.setRequestValues()
  }

  public calculateCol(count: number) {
    if ((count || 12) === 12) {
      return ''
    }
    return Math.floor(12 / (count || 12)) * -1
  }

  @Watch('customerNumber')
  private async customerChanged() {
    this.request.CustomerNumber = this.customerNumber
  }

  public _isCustomerAdminSite() {
    return isCustomerAdminSite(this.role)
  }

  public getRows(tabTitle: string) {
    if (this.configuration.Layout) {
      return this.configuration.Layout.Rows.filter(row => !row.Tab || row.Tab === tabTitle)
    }
    return []
  }

  public getTabs() {
    if (this.configuration.Layout && this.configuration.Layout.Tabs && this.configuration.Layout.Tabs.length > 0) {
      return this.configuration.Layout.Tabs.filter((tab: any) => !tab.OnlyVisibleForCustomerAdmin || this._isCustomerAdminSite())
    }
    return [{ Title: 'General', OnlyVisibleForCustomerAdmin: false }]
  }

  public clearAll() {
    this.request.UserValues = []
    this.setOverlayShowing(true)
    this.setDivisionCode()
    this.getConfiguration().then(() => {
      this.setOverlayShowing(false)
    })
  }

  public async displayErrors() {
    const errorKeys = this.configuration.IncompleteCharacteristics.concat(this.configuration.InconsistentCharacteristics)
    const error = errorKeys.filter(s => this.configuration.Characteristics[s]).map(s => this.configuration.Characteristics[s].CharacteristicText + ' [' + s + ']').join('\r\n')
    this.displayError(error)
  }

  public async displayConstraints() {
    if (this.configuration.ConstraintConflicts && this.configuration.ConstraintConflicts.length > 0) {
      const errorKeys = this.configuration.ConstraintConflicts
      const error = errorKeys.map(s => this.configuration.ConstraintConflictTexts[s] || 'Constraint ' + s + ' is violated').join('\r\n')
      this.displayError(error)
    }
  }

  @Watch('configurationLoaded')
  private async updateConfig() {
    this.configurableMaterial.OrderItems = this.configuration.OrderItems
    this.configurableMaterial.Incomplete = this.configuration.IncompleteCharacteristics.length
    this.configurableMaterial.Inconsistent = this.configuration.InconsistentCharacteristics.length
    this.configurableMaterial.UserValues = this.request.UserValues
    const visibleCaharacteristics = this.configuration.Values
      .filter(value => this.configuration.Invisible.indexOf(value.Characteristic) < 0 && !this.configuration.Characteristics[value.Characteristic].Hidden)
      .map(s => s.Characteristic)

    const newVisibles = visibleCaharacteristics.filter(item => !this.currentVisibleCharacteristics.includes(item))
    let warningMessages: any[] = []
    let errorMessages: any[] = []
    if (this.configuration.Layout) {
      this.configuration.Layout.Rows.forEach((row: any) => {
        if (row.Warning) {
          const warningItems = row.Items.filter(item => newVisibles.includes(item))
          if (warningItems.length > 0) {
            let texts = warningItems.map(item => {
              const value = this.getConfigurationValue(item, null)
              if (value.Values.length > 0) {
                return this.getTextValue(value.Values, this.configuration.Characteristics[value.Characteristic].Values)
              }
              return []
            })
            if (texts.length > 0) {
              texts = texts.reduce((a, b) => a.concat(b))
            }
            warningMessages = [...warningMessages, ...texts.map(s => s.ValueText)]
          }
        }
        if (row.Error) {
          const errorItems = row.Items.filter(item => newVisibles.includes(item))
          if (errorItems.length > 0) {
            let texts = errorItems.map(item => {
              const value = this.getConfigurationValue(item, null)
              if (value.Values.length > 0) {
                return this.getTextValue(value.Values, this.configuration.Characteristics[value.Characteristic].Values)
              }
              return []
            })
            if (texts.length > 0) {
              texts = texts.reduce((a, b) => a.concat(b))
            }
            errorMessages = [...warningMessages, ...texts.map(s => s.ValueText)]
          }
        }
      })
    }
    this.currentVisibleCharacteristics = visibleCaharacteristics
    this.configurableMaterial.WarningMessages = warningMessages
    this.configurableMaterial.ErrorMessages = errorMessages
    this.displayToasts()
    this.displayConstraints()
    this.updateSpecialPrices()
    if (this.configuration.ConstraintConflicts.length > 0) {
      this.$nextTick(() => {
        this.removeLastValue()
      })
    }
  }

  public removeLastValue() {
    const lastValue = this.request.UserValues[this.request.UserValues.length - 1]
    if (lastValue) {
      if (lastValue.Values.length > 0) {
        lastValue.Values.splice(lastValue.Values.length - 1, 1)
      } else {
        this.request.UserValues.splice(this.request.UserValues.length - 1, 1)
      }
      this.setOverlayShowing(true)
      this.getConfiguration().then(() => {
        this.setOverlayShowing(false)
      })
    }
  }

  public getTextValue(values: string[], options: any[]) {
    return options.filter(s => values.indexOf(s.Value) >= 0)
  }

  @Watch('configurableMaterial.SpecialPrices', { deep: true })
  private async updateSpecialPrices() {
    this.configurableMaterial.TotalPrice = 0
    if (this.configurableMaterial.SpecialPrices.length > 0) {
      for (let index = 0; index < this.configurableMaterial.SpecialPrices.length; index++) {
        const element = this.configurableMaterial.SpecialPrices[index]
        const multiplier = element.SpecialMultiplier < 0 ? 100 + element.SpecialMultiplier * 1 : element.SpecialMultiplier
        const price = (Math.round(((element.SpecialPrice * multiplier / 100)) * 100) / 100)
        this.configurableMaterial.TotalPrice += price
      }
    } else {
      for (let index = 0; index < this.configurableMaterial.OrderItems.length; index++) {
        const element = this.configurableMaterial.OrderItems[index]
        this.configurableMaterial.TotalPrice += parseFloat(element.NetValue1)
      }
    }
    this.$emit('input', this.configurableMaterial)
  }

  public displayToasts() {
    this.configurableMaterial.WarningMessages.filter(s => s).forEach(text => {
      this.displayWarning(text)
    })
    this.configurableMaterial.ErrorMessages.filter(s => s).forEach(text => {
      this.displayError(text)
    })
  }

  public displayError(text: string) {
    (this as any).$bvToast.toast(text, {
      title: 'Error',
      variant: 'danger',
      solid: true,
      toaster: 'b-toaster-top-center',
      autoHideDelay: 30000,
    })
  }

  public displayWarning(text: string) {
    (this as any).$bvToast.toast(text, {
      title: 'Warning',
      variant: 'warning',
      solid: true,
      toaster: 'b-toaster-top-center',
      autoHideDelay: 30000,
    })
  }

  public checkVisibility(row) {
    return (row.Note && row.Items.length === 0) || (row.Items.filter(s => this.getConfigurationValue(s, null) && this.configuration.Invisible.indexOf(s) < 0 && !this.configuration.Characteristics[s].Hidden).length > 0)
  }

  private collectValues(from: ISapConfiguratorValue[], to: ISapConfiguratorValue[]) {
    for (let index = 0; index < from.length; index++) {
      const element = from[index]
      const userValue: ISapConfiguratorValue = {
        Characteristic: element.Characteristic,
        Values: element.Values,
      }
      to.push(userValue)
    }
  }

  public materialChange(materialNumber) {
    this.configurableMaterial.MaterialNumber = materialNumber
    this.setRequestValues()
  }

  private setRequestValues() {
    this.clearConfiguration()
    this.resetRequest()
    this.request.MaterialNumber = this.configurableMaterial.MaterialNumber
    this.request.UserValues = this.configurableMaterial.UserValues
    this.setDivisionCode()
    this.request.CustomerNumber = this.customerNumber
    if (this.configurableMaterial.MaterialNumber && this.configurableMaterial.MaterialNumber.length > 0) {
      this.getConfiguration()
    }
  }

  private setDivisionCode() {
    if (this.request.UserValues.length === 0 && this.divisionCode) {
      this.request.UserValues.push({
        Characteristic: 'CH_REF_VBAK_KVGR3',
        Values: [this.divisionCode],
      })
    }
  }

  public getConfigurationValue(characteristic: string, valueList: any) {
    const values = valueList ?? this.configuration.Values
    for (let index = 0; index < values.length; index++) {
      const value = values[index]
      if (value.Characteristic === characteristic) {
        return value
      }
    }
    return null
  }

  public async handleAddItemToOrder(item: Model, quantity: number) {
    const price = this.getMultiplierPrice(item.PcnCatalogNumber, item.ProductHierarchy, item.TotalList)
    const cartItem: CartItem = {
      pcnCatalogNumber: item.PcnCatalogNumber || '',
      productHierarchy: item.ProductHierarchy || '',
      fingoodTpiMfgMod: item.FingoodTpiMfgMod || '',
      description:
        item.SapModelDescription || item.Details.Description?.toString() || '',
      originalPrice: parseFloat(item.TotalList.toString()) || 0,
      price: price || 0,
      totalPrice: price * parseInt(quantity.toString()),
      quantity: parseInt(quantity.toString()),
      imgThumbnail: item.Image?.ImagePath || '',
      inventory: item.Available,
      modelDetails: item.Details,
      leadTime: item.LeadTime || 0,
    }
    // check for valid price
    this.saveCartItem({ cartItem })
      .then(() => {
        this.toastSuccess('Item Added to Order1111111111')
      })
      .catch(() => {
        this.toastError('Fail to add item to Order')
      })
  }

  public getValue(values: string[], options: any[]) {
    return options.filter(s => values.indexOf(s.Value) >= 0)
  }
}
</script>
<style>
.custom-border-container {
    border: 2px solid #dc3545; /* Bootstrap red rengi */
    padding: 25px 15px 15px 15px;
    position: relative;
    border-radius: 4px;
    margin-top: 20px;
}

.custom-border-title {
    position: absolute;
    top: -16px;
    left: 20px;
    background-color: white; /* Yazının arkasındaki çizgiyi gizlemek için */
    padding: 0 10px;
    color: #dc3545;
    font-weight: bold;
    font-size: 1.1rem;
    text-transform: uppercase;
}
</style>
